import moment from 'moment';
import { useCallback, useContext, useMemo } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { getCurrentUserWorkspaceUserMembershipsFunc, getWorkspaceScoreFunc, refreshWorkspaceScoreFunc } from '../lib/helper';
import { Dashboard, Workspace, WorkspaceScore, WorkspaceScoreItem, WorkspaceUserMembership } from '../types/files';
import useWorkspacesContext from './context/useWorkspacesContext';

const useWorkspaces = () => {
  const { userGroup} = useContext(AuthContext);
  const { workspaceScores, membershipsStatusGroups, setWorkspaceUserMemberships } = useWorkspacesContext();

  const { acceptedWorkspaces, pendingWorkspaces } = useMemo(() => membershipsStatusGroups, [membershipsStatusGroups]);

  const fetchWorkspaceUserMemberships = useCallback(async () => {
    const membershipsData = await getCurrentUserWorkspaceUserMembershipsFunc();
    const membershipItems = membershipsData as unknown as {items: WorkspaceUserMembership[]};

    setWorkspaceUserMemberships(membershipItems?.items || []);
  }, [setWorkspaceUserMemberships]);

  const refreshScores = useCallback((workspaces: Workspace[], dashboards: Dashboard[], postRefresh?: () => void) => {
    Promise.all(workspaces.flatMap(workspace =>
      dashboards.map(dashboard => (
        refreshWorkspaceScoreFunc({
          dashboardId: dashboard.id,
          workspaceId: workspace.id,
          group: userGroup,
        })
      ))
    )).then(postRefresh);
  }, [userGroup]);

  const parseWorkspaceScores = useCallback((scores?: any) => {
    if (typeof scores === 'string') {
      const parsedScores = JSON.parse(scores?.replace('scores', '') || '{}') || {};

      return Object.keys(parsedScores).map(dashboardId => ({
          dashboardId,
          score: parsedScores[dashboardId]
      }));
    } else if (Array.isArray(scores)) {
      return scores;
    }

    return [];
  }, []);

  const computeWorkspaceScores = useCallback(async (workspaces: Workspace[], group?: string) => {
    const workspaceScoresData = await Promise.all(workspaces.map((workspace) =>
      getWorkspaceScoreFunc({ workspaceId: workspace.id || '', group: group ?? userGroup }))
    );
    let updatedWorkspaceScores: WorkspaceScore[] = [...workspaceScores];

    workspaceScoresData.forEach(newWsScores => {
      if (!updatedWorkspaceScores.some(prevScore => prevScore.workspaceId === newWsScores?.workspaceId)) {
        const parsedScores: WorkspaceScoreItem[] = [...parseWorkspaceScores(newWsScores?.scores) ?? []];

        updatedWorkspaceScores.push(({ ...newWsScores, scores: parsedScores }) as WorkspaceScore);
      } else {
        updatedWorkspaceScores = updatedWorkspaceScores.map((prevScore: any) => {
          if (prevScore.workspaceId === newWsScores?.workspaceId) {
            const mergedScores: WorkspaceScoreItem[] = [...parseWorkspaceScores(prevScore.scores) ?? []];
            const newScores: WorkspaceScoreItem[] = [...parseWorkspaceScores(newWsScores?.scores) ?? []];

            newScores.forEach(newScore => {
              const existingScoreIndex = mergedScores.findIndex(score => score.dashboardId === newScore.dashboardId);

              if (existingScoreIndex !== -1)
                mergedScores[existingScoreIndex] = newScore;
              else
                mergedScores.push(newScore);
            });

            const updatedAt = moment(newWsScores?.updatedAt);
            const prevUpdatedAt = moment(prevScore.updatedAt);

            return updatedAt.isAfter(prevUpdatedAt)
              ? { ...prevScore, ...newWsScores, scores: mergedScores }
              : { ...prevScore, scores: mergedScores };
          }

          return prevScore;
        });
      }
    });

    return updatedWorkspaceScores;
  }, [parseWorkspaceScores, userGroup, workspaceScores]);

  return { acceptedWorkspaces, pendingWorkspaces, refreshScores, parseWorkspaceScores, computeWorkspaceScores, fetchWorkspaceUserMemberships };
};

export default useWorkspaces;